import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Share2, FileText, LayoutGrid, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { fetchAllQuotations, deleteQuotation } from './quotationQueries';
import { formatCurrency, formatDate } from '@/lib/utils';

const PAGE_SIZE = 10;

export function QuotationsPage() {
  const qc = useQueryClient();
  const [view, setView] = useState<'table' | 'card'>('table');
  const [page, setPage] = useState(1);

  const { data: quotations = [], isLoading } = useQuery({
    queryKey: ['quotations'],
    queryFn: fetchAllQuotations,
  });

  const remove = useMutation({
    mutationFn: deleteQuotation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quotations'] });
      toast({ title: 'Quotation deleted', variant: 'success' });
    },
  });

  const shareQuotation = async (token: string) => {
    const url = `${window.location.origin}/q/${token}`;
    await navigator.clipboard.writeText(url);
    toast({ title: 'Share link copied', variant: 'success' });
  };

  // Pagination
  const totalPages = Math.max(1, Math.ceil(quotations.length / PAGE_SIZE));
  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return quotations.slice(start, start + PAGE_SIZE);
  }, [quotations, page]);

  const QuotationNumber = ({ q }: { q: typeof quotations[0] }) => (
    <span className="font-medium">
      {q.quotationNumber ?? q.id.slice(0, 8).toUpperCase()}
    </span>
  );

  const getTimestamp = (q: typeof quotations[0]) =>
    // @ts-expect-error firestore timestamp
    typeof q.createdAt?.toMillis === 'function' ? q.createdAt.toMillis() : q.createdAt;

  return (
    <div>
      <PageHeader
        title="Quotations"
        subtitle="Drafts and finalised quotations."
        actions={
          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex rounded-md border bg-muted p-0.5">
              <button
                onClick={() => setView('table')}
                className={cn(
                  'flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-medium transition-colors',
                  view === 'table'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                title="Table view"
              >
                <List className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Table</span>
              </button>
              <button
                onClick={() => setView('card')}
                className={cn(
                  'flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-medium transition-colors',
                  view === 'card'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                title="Card view"
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Cards</span>
              </button>
            </div>
            <Button asChild>
              <Link to="/quotations/new">
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">New quotation</span>
              </Link>
            </Button>
          </div>
        }
      />

      {isLoading && <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>}

      {!isLoading && quotations.length === 0 && (
        <div className="p-10 text-center text-sm text-muted-foreground">
          No quotations yet. Build your first one.
        </div>
      )}

      {/* ── TABLE VIEW ── */}
      {!isLoading && quotations.length > 0 && view === 'table' && (
        <Card>
          {/* Header */}
          <div className="grid grid-cols-12 border-b bg-muted/40 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <div className="col-span-4 px-4 py-2.5 border-r">Quotation</div>
            <div className="col-span-3 px-4 py-2.5 border-r">Customer</div>
            <div className="col-span-2 px-4 py-2.5 border-r text-right">Total</div>
            <div className="col-span-1 px-4 py-2.5 border-r text-center">Status</div>
            <div className="col-span-2 px-4 py-2.5 text-right">Actions</div>
          </div>

          <div className="divide-y">
            {paginated.map((q) => (
              <div key={q.id} className="grid grid-cols-12 text-sm hover:bg-accent/20 transition-colors">
                {/* Quotation number + date */}
                <Link
                  to={`/quotations/${q.id}`}
                  className="col-span-4 flex items-center gap-2 min-w-0 px-4 py-3 border-r hover:underline"
                >
                  <FileText className="h-3.5 w-3.5 shrink-0 text-gold-600" />
                  <div className="min-w-0">
                    <QuotationNumber q={q} />
                    <div className="text-[11px] text-muted-foreground truncate">
                      {formatDate(getTimestamp(q))}
                    </div>
                  </div>
                </Link>

                {/* Customer */}
                <div className="col-span-3 px-4 py-3 border-r flex items-center truncate text-xs text-muted-foreground">
                  {q.customerSnapshot?.name ?? '—'}
                </div>

                {/* Total */}
                <div className="col-span-2 px-4 py-3 border-r flex items-center justify-end tabular-nums font-medium">
                  {formatCurrency(q.totalAmount)}
                </div>

                {/* Status */}
                <div className="col-span-1 px-4 py-3 border-r flex items-center justify-center">
                  <Badge variant={q.status === 'final' ? 'gold' : 'muted'} className="capitalize">
                    {q.status}
                  </Badge>
                </div>

                {/* Actions */}
                <div className="col-span-2 px-4 py-3 flex items-center justify-end gap-1">
                  {q.shareToken && q.status === 'final' && (
                    <Button variant="ghost" size="icon" onClick={() => shareQuotation(q.shareToken!)} title="Copy share link">
                      <Share2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => { if (confirm('Delete this quotation?')) remove.mutate(q.id); }}
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── CARD VIEW ── */}
      {!isLoading && quotations.length > 0 && view === 'card' && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {paginated.map((q) => (
            <Card key={q.id} className="flex flex-col p-4 hover:shadow-md transition-shadow">
              {/* Top row */}
              <div className="flex items-start justify-between gap-2">
                <Link to={`/quotations/${q.id}`} className="min-w-0 flex-1 hover:underline">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-gold-600" />
                    <QuotationNumber q={q} />
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {formatDate(getTimestamp(q))}
                  </div>
                </Link>
                <Badge variant={q.status === 'final' ? 'gold' : 'muted'} className="shrink-0 capitalize">
                  {q.status}
                </Badge>
              </div>

              {/* Customer */}
              <div className="mt-3 text-sm">
                <span className="text-muted-foreground text-xs">Customer</span>
                <div className="font-medium truncate">{q.customerSnapshot?.name ?? '—'}</div>
                {q.customerSnapshot?.phone && (
                  <div className="text-xs text-muted-foreground">{q.customerSnapshot.phone}</div>
                )}
              </div>

              {/* Total */}
              <div className="mt-3 flex items-center justify-between border-t pt-3">
                <span className="text-xs text-muted-foreground">Total</span>
                <span className="font-serif text-lg tabular-nums">{formatCurrency(q.totalAmount)}</span>
              </div>

              {/* Actions */}
              <div className="mt-2 flex justify-end gap-1">
                {q.shareToken && q.status === 'final' && (
                  <Button variant="ghost" size="icon" onClick={() => shareQuotation(q.shareToken!)} title="Copy share link">
                    <Share2 className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { if (confirm('Delete this quotation?')) remove.mutate(q.id); }}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ── PAGINATION ── */}
      {!isLoading && quotations.length > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, quotations.length)} of {quotations.length}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <Button
                key={p}
                variant={p === page ? 'default' : 'outline'}
                size="icon"
                className="h-8 w-8 text-xs"
                onClick={() => setPage(p)}
              >
                {p}
              </Button>
            ))}
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Mobile card list (always card on mobile regardless of toggle) */}
      {/* Already handled above — card view is default on mobile via the toggle */}
    </div>
  );
}
